import { useEffect, useState } from "react";
import {
  Users, Beaker, CalendarClock, UserCheck, TrendingUp, Target,
  ArrowUpRight, AlertCircle, Sparkles, Factory, GitBranch, Link2, ShieldCheck,
  Award, ShoppingCart, Package,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import WelcomeEditor from "@/components/WelcomeEditor";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import { formatEuro, PARTNER_LEVEL_COLORS } from "@/lib/constants";

const KpiCard = ({ icon: Icon, label, value, accent = "#1F4D3A", testId, hint }) => (
  <div
    className="card-soft card-soft-hover p-5 flex flex-col gap-3 kpi-pop"
    data-testid={testId}
  >
    <div className="flex items-center justify-between">
      <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#6C757D]">
        {label}
      </div>
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: `${accent}14`, color: accent }}
      >
        <Icon size={18} />
      </div>
    </div>
    <div className="font-display font-bold text-3xl text-[#222] leading-none">{value}</div>
    {hint && <div className="text-xs text-[#6C757D]">{hint}</div>}
  </div>
);

const RevenueBreakdown = ({ icon: Icon, title, data, accent, testId }) => {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  const max = entries.length ? entries[0][1] : 0;
  return (
    <div className="card-soft p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} style={{ color: accent }} />
        <h3 className="font-display font-semibold text-base text-[#222]">{title}</h3>
      </div>
      {entries.length === 0 ? (
        <div className="text-xs text-[#6C757D] py-4 text-center">No order data in the last 30 days.</div>
      ) : (
        <ul className="space-y-2">
          {entries.slice(0, 6).map(([k, v]) => (
            <li key={k} className="flex items-center gap-3" data-testid={`${testId}-row-${k}`}>
              <div className="text-sm font-semibold text-[#222] w-40 truncate">{k}</div>
              <div className="flex-1 h-2 rounded-full bg-[#E5E5E5] overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${(v / max) * 100}%`, backgroundColor: accent }} />
              </div>
              <div className="text-sm font-semibold text-[#1F4D3A] w-24 text-right">{formatEuro(v)}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};


export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [followups, setFollowups] = useState([]);
  const [seeding, setSeeding] = useState(false);
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";

  const load = async () => {
    try {
      const [s, f] = await Promise.all([api.getDashboard(), api.listFollowUps()]);
      setStats(s);
      setFollowups(f.slice(0, 5));
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSeed = async () => {
    if (!window.confirm(
      "⚠️ Load Demo Data?\n\nThis will populate the system with sample customers, leads, orders and batches. " +
      "Use ONLY in demo / testing environments. If this is your production system, click Cancel."
    )) return;
    setSeeding(true);
    try {
      const r = await api.seed(false);
      if (r.seeded) toast.success("Demo data loaded");
      else toast.info("Data already exists — seed skipped");
      await load();
    } catch {
      toast.error("Failed to seed data");
    } finally {
      setSeeding(false);
    }
  };

  if (!stats) {
    return (
      <div className="text-sm text-[#6C757D]" data-testid="dashboard-loading">
        Loading dashboard…
      </div>
    );
  }

  const goalPct = stats.goal_progress_percent || 0;
  const coverage = stats.traceability_coverage_percent ?? 0;

  return (
    <div data-testid="page-dashboard">
      <PageHeader
        overline="Garnacha · Horeca"
        title="Dashboard"
        subtitle="Track leads, samples and revenue toward your €10,000 monthly goal."
        actions={
          isSuperAdmin && (
            <Button
              onClick={handleSeed}
              disabled={seeding}
              data-testid="seed-demo-btn"
              className="bg-white text-[#222] border border-[#E5E5E5] hover:bg-[#F7F1E8] hover:text-[#E85D04]"
              title="Super admin only — populates the DB with sample data"
            >
              <Sparkles size={16} className="mr-2" />
              Load Demo Data
            </Button>
          )
        }
      />

      <WelcomeEditor />

      {/* Goal progress */}
      <div className="card-soft p-6 mb-6" data-testid="goal-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="brand-overline">Primary Goal</div>
            <div className="font-display font-bold text-2xl text-[#222] mt-1">
              €10,000 / month in recurring sales
            </div>
            <div className="text-sm text-[#6C757D] mt-1">
              Currently at <span className="text-[#222] font-semibold">{formatEuro(stats.actual_monthly_revenue)}</span>
              {" "}— pipeline potential <span className="text-[#E85D04] font-semibold">{formatEuro(stats.potential_monthly_revenue)}</span>
            </div>
          </div>
          <div className="text-right">
            <div
              className="font-display font-bold text-4xl"
              style={{ color: goalPct >= 100 ? "#2A9D8F" : "#E85D04" }}
              data-testid="goal-percent"
            >
              {goalPct.toFixed(0)}%
            </div>
            <div className="text-xs uppercase tracking-[0.22em] text-[#6C757D] font-semibold">Goal Progress</div>
          </div>
        </div>
        <div className="mt-5 w-full h-3 rounded-full bg-[#E5E5E5] overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{
              width: `${Math.min(goalPct, 100)}%`,
              backgroundColor: goalPct >= 100 ? "#2A9D8F" : "#E85D04",
            }}
            data-testid="goal-progress-bar"
          />
        </div>
      </div>

      {/* Sales KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <KpiCard icon={Users} label="Total Leads" value={stats.total_leads} accent="#457B9D" testId="kpi-leads" />
        <KpiCard icon={Beaker} label="Samples Delivered" value={stats.samples_delivered} accent="#7B6DCC" testId="kpi-samples" />
        <KpiCard icon={CalendarClock} label="Follow Ups Pending" value={stats.follow_ups_pending} accent="#E76F51" testId="kpi-followups" />
        <KpiCard icon={UserCheck} label="Active Customers" value={stats.active_customers} accent="#2A9D8F" testId="kpi-customers" />
        <KpiCard
          icon={TrendingUp}
          label="Potential Revenue"
          value={formatEuro(stats.potential_monthly_revenue)}
          accent="#E85D04"
          testId="kpi-potential"
          hint="Pipeline + active customers"
        />
        <KpiCard
          icon={Target}
          label="Actual Monthly Revenue"
          value={formatEuro(stats.actual_monthly_revenue)}
          accent="#2A9D8F"
          testId="kpi-actual"
          hint={`${formatEuro(stats.estimated_monthly_volume)}L est. volume`}
        />
      </div>

      {/* Traceability KPIs */}
      <div className="card-soft p-5 mb-6" data-testid="traceability-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <GitBranch size={16} className="text-[#E85D04]" />
            <div>
              <div className="brand-overline">Food Traceability</div>
              <h2 className="font-display font-semibold text-xl text-[#222]">Production lineage</h2>
            </div>
          </div>
          <Link
            to="/traceability"
            className="text-sm text-[#E85D04] font-semibold inline-flex items-center gap-1 hover:underline"
            data-testid="see-all-trace"
          >
            Open traceability <ArrowUpRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard icon={Factory} label="Production Batches" value={stats.total_production_batches ?? 0} accent="#E85D04" testId="kpi-batches" />
          <KpiCard icon={Link2} label="Samples Linked" value={stats.samples_linked_to_batches ?? 0} accent="#7B6DCC" testId="kpi-samples-linked" />
          <KpiCard icon={Link2} label="Customers Linked" value={stats.customers_linked_to_batches ?? 0} accent="#2A9D8F" testId="kpi-customers-linked" />
          <KpiCard
            icon={ShieldCheck}
            label="Coverage"
            value={`${coverage.toFixed(1)}%`}
            accent="#1F4D3A"
            testId="kpi-coverage"
            hint="Samples + customers with batch links"
          />
        </div>
        <div className="mt-5 w-full h-2.5 rounded-full bg-[#E5E5E5] overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{
              width: `${Math.min(coverage, 100)}%`,
              backgroundColor: "#2A9D8F",
            }}
            data-testid="coverage-progress-bar"
          />
        </div>
      </div>

      {/* Partner level breakdown */}
      <div className="card-soft p-5 mb-6" data-testid="partner-card">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Award size={16} className="text-[#C9A02A]" />
            <div>
              <div className="brand-overline">Partner Program</div>
              <h2 className="font-display font-semibold text-xl text-[#222]">Customers by partner level</h2>
            </div>
          </div>
          <Link
            to="/customers"
            className="text-sm text-[#E85D04] font-semibold inline-flex items-center gap-1 hover:underline"
            data-testid="see-all-customers"
          >
            Manage customers <ArrowUpRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-3 gap-4">
          {["Standard", "Partner Level 1", "Partner Level 2"].map((lvl) => {
            const count = (stats.customers_by_partner_level || {})[lvl] || 0;
            const total = stats.total_customers || 0;
            const pct = total ? Math.round((count / total) * 100) : 0;
            const color = PARTNER_LEVEL_COLORS[lvl];
            return (
              <div
                key={lvl}
                className="rounded-xl p-4 border"
                style={{ backgroundColor: `${color}10`, borderColor: `${color}33` }}
                data-testid={`partner-tier-${lvl.replaceAll(" ", "-")}`}
              >
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color }}>{lvl}</div>
                <div className="font-display font-bold text-3xl text-[#222] mt-1">{count}</div>
                <div className="text-xs text-[#6C757D] mt-0.5">{pct}% · {formatEuro((stats.revenue_by_partner_level || {})[lvl] || 0)} /mo</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Orders & revenue stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <RevenueBreakdown
          icon={Package}
          title="Revenue by Product"
          data={stats.revenue_by_product || {}}
          accent="#E85D04"
          testId="revenue-by-product"
        />
        <RevenueBreakdown
          icon={ShoppingCart}
          title="Revenue by Format"
          data={stats.revenue_by_product_format || {}}
          accent="#2A9D8F"
          testId="revenue-by-format"
        />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <KpiCard
          icon={ShoppingCart}
          label="Monthly Revenue (Orders)"
          value={formatEuro(stats.monthly_revenue_orders || 0)}
          accent="#E85D04"
          testId="kpi-monthly-orders"
          hint="Sum of all orders in the last 30 days"
        />
        <KpiCard
          icon={Users}
          label="Total Customers"
          value={stats.total_customers ?? 0}
          accent="#457B9D"
          testId="kpi-total-customers"
        />
        <KpiCard
          icon={TrendingUp}
          label="Avg Revenue / Customer"
          value={formatEuro(stats.avg_revenue_per_customer || 0)}
          accent="#1F4D3A"
          testId="kpi-avg-rev-customer"
          hint="Last 30 days"
        />
      </div>

      {/* Upcoming follow ups */}
      <div className="card-soft p-5" data-testid="upcoming-followups">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="brand-overline">Action Needed</div>
            <h2 className="font-display font-semibold text-xl text-[#222]">Upcoming Follow Ups</h2>
          </div>
          <Link
            to="/followups"
            className="text-sm text-[#E85D04] font-semibold inline-flex items-center gap-1 hover:underline"
            data-testid="see-all-followups"
          >
            See all <ArrowUpRight size={14} />
          </Link>
        </div>
        {followups.length === 0 ? (
          <div className="text-sm text-[#6C757D] py-6 text-center">
            <AlertCircle size={18} className="inline mr-2 text-[#E76F51]" />
            No upcoming follow ups. Add a date on any lead or sample.
          </div>
        ) : (
          <ul className="divide-y divide-[#E5E5E5]">
            {followups.map((f) => (
              <li
                key={f.id}
                className="flex items-center justify-between py-3 gap-3"
                data-testid={`dashboard-followup-${f.id}`}
              >
                <div className="min-w-0">
                  <div className="font-semibold text-[#222] truncate">{f.company}</div>
                  <div className="text-xs text-[#6C757D]">
                    {f.reason} · {f.contact_person || "—"}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-semibold text-[#E85D04]">
                    {new Date(f.follow_up_date).toLocaleDateString("en-GB")}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-[#6C757D]">{f.source}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
